/* EPIDEN owned-store carousel. No tracking or third-party dependencies. */
(() => {
  function swipeStep(start, end) {
    const dx = end.x - start.x, dy = end.y - start.y;
    return Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy) * 1.5 ? (dx < 0 ? 1 : -1) : 0;
  }
  class CarouselState {
    constructor(count, reducedMotion) {
      this.count = count; this.index = 0; this.paused = reducedMotion;
      this.inView = false; this.hidden = false; this.hovered = false; this.focused = false;
    }
    canRun() { return this.count > 1 && this.inView && !this.paused && !this.hidden && !this.hovered && !this.focused; }
    // Explicit Play overrides existing focus/hover; a new interaction pauses again.
    play() { this.paused = false; this.focused = false; this.hovered = false; }
    select(index, manual = false) {
      this.index = ((index % this.count) + this.count) % this.count;
      if (manual) this.paused = true;
    }
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { CarouselState, swipeStep };
  if (typeof customElements === 'undefined' || customElements.get('epiden-carousel')) return;
  class EpidenCarousel extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.slides = [...this.querySelectorAll('[data-slide]')];
      if (!this.slides.length) return;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.finePointer = matchMedia('(hover: hover) and (pointer: fine)');
      this.state = new CarouselState(this.slides.length, this.reduced.matches);
      this.state.hidden = document.hidden;
      this.events = new AbortController();
      const on = (target, type, fn) => target.addEventListener(type, fn, { signal: this.events.signal });
      this.controls = this.querySelector('[data-controls]');
      this.toggle = this.querySelector('[data-play]');
      this.tabs = [...this.querySelectorAll('[data-select]')];
      this.status = this.querySelector('[data-status]');
      this.track = this.querySelector('.ep-spotlight__slides');
      this.interval = Math.min(10000, Math.max(3000, Number(this.dataset.interval) || 5000));
      // Wait briefly so merely crossing a dot does not change the slide.
      this.tabs.forEach(tab => {
        on(tab, 'pointerenter', event => {
          this.cancelHover();
          if (event.pointerType !== 'mouse' || !this.finePointer.matches || this.reduced.matches || !tab.getClientRects().length) return;
          this.hoverTimer = setTimeout(() => this.show(Number(tab.dataset.select), true), 200);
        });
        on(tab, 'pointerleave', () => this.cancelHover());
      });
      on(this.track, 'pointerdown', event => {
        if (event.pointerType !== 'touch') return;
        this.touchStart = { x: event.clientX, y: event.clientY };
        this.state.paused = true; this.update();
      });
      on(this.track, 'pointerup', event => {
        if (!this.touchStart) return;
        const step = swipeStep(this.touchStart, { x: event.clientX, y: event.clientY });
        this.touchStart = null;
        if (step) this.show(this.state.index + step, true);
        else this.update();
      });
      on(this.track, 'pointercancel', () => { this.touchStart = null; this.update(); });
      on(this, 'click', event => {
        this.cancelHover();
        const control = event.target.closest('button');
        if (!control || !this.contains(control)) return;
        if (control.hasAttribute('data-select')) this.show(Number(control.dataset.select), true);
        if (control.hasAttribute('data-step')) this.show(this.state.index + Number(control.dataset.step), true);
        if (control === this.toggle) {
          if (this.state.paused) this.state.play(); else this.state.paused = true;
          this.update();
        }
      });
      on(this.controls, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.state.index + (event.key === 'ArrowRight' ? 1 : -1);
        this.show(index, true);
        this.tabs[this.state.index].focus();
      });
      on(this, 'pointerenter', event => { if (event.pointerType !== 'touch') { this.state.hovered = true; this.update(); } });
      on(this, 'pointerleave', () => { this.cancelHover(); this.state.hovered = false; this.update(); });
      on(this, 'focusin', event => { this.cancelHover(); this.state.focused = true; if (!event.target.closest('[data-play]')) this.state.paused = true; this.update(); });
      on(this, 'focusout', event => { this.state.focused = this.contains(event.relatedTarget); this.update(); });
      on(document, 'visibilitychange', () => { this.cancelHover(); this.state.hidden = document.hidden; this.update(); });
      on(this.reduced, 'change', event => { this.cancelHover(); if (event.matches) this.state.paused = true; this.update(); });
      on(this.finePointer, 'change', () => this.cancelHover());
      this.observer = new IntersectionObserver(entries => {
        this.state.inView = entries[0].isIntersecting; this.update();
      }, { threshold: 0.15 });
      this.observer.observe(this);
      this.controls.hidden = this.slides.length < 2;
      this.classList.add('is-ready');
      this.show(0);
    }
    show(index, manual = false) {
      this.cancelHover();
      this.state.select(index, manual);
      this.slides.forEach((slide, i) => {
        slide.hidden = false; slide.inert = i !== this.state.index;
        slide.setAttribute('aria-hidden', String(i !== this.state.index));
      });
      this.tabs.forEach((tab, i) => { tab.setAttribute('aria-pressed', String(i === this.state.index)); });
      if (manual) this.status.textContent = this.slides[this.state.index].getAttribute('aria-label');
      this.update();
      this.dispatchEvent(new CustomEvent('epiden:slide-change'));
    }
    update() {
      clearTimeout(this.timer);
      if (!this.toggle) this.state.paused = this.reduced.matches || Boolean(this.touchStart);
      if (this.toggle) {
        this.toggle.setAttribute('aria-label', this.state.paused ? this.dataset.playLabel : this.dataset.pauseLabel);
        this.toggle.title = this.toggle.getAttribute('aria-label');
        this.toggle.querySelector('[data-pause-icon]').toggleAttribute('hidden', this.state.paused);
        this.toggle.querySelector('[data-play-icon]').toggleAttribute('hidden', !this.state.paused);
      }
      this.dataset.playing = String(this.state.canRun());
      if (this.state.canRun()) this.timer = setTimeout(() => this.show(this.state.index + 1), this.interval);
    }
    cancelHover() { clearTimeout(this.hoverTimer); this.hoverTimer = null; }
    disconnectedCallback() {
      this.cancelHover(); clearTimeout(this.timer); this.events?.abort(); this.observer?.disconnect(); this.events = null;
    }
  }
  customElements.define('epiden-carousel', EpidenCarousel);
})();
